import type { ReactElement, ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { CheckCircle, Circle, CreditCard, Key, Loader2, Shield, XCircle } from 'lucide-react'
import type { Entitlement, GateStatus } from '../../../types'

const STATUS_COLORS: Record<GateStatus, string> = {
  pending:
    'bg-stone-100 border-stone-300 text-stone-500 dark:bg-stone-800 dark:border-stone-600 dark:text-stone-400',
  in_progress:
    'bg-amber-100 border-amber-400 text-amber-600 dark:bg-amber-900/30 dark:border-amber-500 dark:text-amber-400',
  passed:
    'bg-[#4e5e82]/10 border-[#8a9bba] text-[#5c6e94] dark:bg-[#8a9bba]/15 dark:border-[#4e5e82] dark:text-[#8a9bba]',
  failed:
    'bg-red-100 border-red-400 text-red-600 dark:bg-red-900/30 dark:border-red-500 dark:text-red-400',
}

const CONNECTOR_COLORS: Record<GateStatus, string> = {
  pending: 'bg-stone-300 dark:bg-stone-600',
  in_progress: 'bg-amber-400 dark:bg-amber-500',
  passed: 'bg-[#8a9bba] dark:bg-[#4e5e82]',
  failed: 'bg-red-400 dark:bg-red-500',
}

function StatusIcon({ status }: { status: GateStatus }): ReactElement {
  switch (status) {
    case 'passed':
      return <CheckCircle className="h-5 w-5" />
    case 'in_progress':
      return <Loader2 className="h-5 w-5 animate-spin" />
    case 'failed':
      return <XCircle className="h-5 w-5" />
    default:
      return <Circle className="h-5 w-5" />
  }
}

interface GateNodeProps {
  label: string
  status: GateStatus
  icon: ReactNode
  detail?: string | undefined
  timestamp?: string | null | undefined
  isLast?: boolean
  t: TFunction
}

export function GateNode({
  label,
  status,
  icon,
  detail,
  timestamp,
  isLast = false,
  t,
}: GateNodeProps): ReactElement {
  const statusLabel =
    status === 'passed'
      ? t('dualGate.passed')
      : status === 'in_progress'
        ? t('dualGate.processing')
        : t('dualGate.pending')
  return (
    <div className="flex items-center">
      <div className="flex flex-col items-center">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-full border-2 ${STATUS_COLORS[status]} transition-all duration-300`}
        >
          <div className="flex flex-col items-center">{icon}</div>
        </div>
        <div className="mt-2 text-center">
          <p className="text-sm font-medium text-stone-700 dark:text-stone-300">{label}</p>
          <div className="mt-1 flex items-center justify-center gap-1 text-xs">
            <StatusIcon status={status} />
            <span
              className={
                status === 'passed'
                  ? 'text-[#5c6e94] dark:text-[#8a9bba]'
                  : 'text-stone-500 dark:text-stone-400'
              }
            >
              {statusLabel}
            </span>
          </div>
          {detail && <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">{detail}</p>}
          {timestamp && (
            <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
              {new Date(timestamp).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      {!isLast && (
        <div className="mx-4 flex items-center">
          <div
            className={`h-1 w-12 rounded-full ${CONNECTOR_COLORS[status]} transition-all duration-300`}
          />
          <div className="h-0 w-0 border-y-4 border-l-8 border-y-transparent border-l-stone-300 dark:border-l-stone-600" />
        </div>
      )}
    </div>
  )
}

interface SummaryFieldProps {
  label: string
  value: ReactNode
}

function SummaryField({ label, value }: SummaryFieldProps): ReactElement {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-stone-500 uppercase dark:text-stone-400">
        {label}
      </p>
      <p className="mt-1 font-medium text-stone-900 dark:text-white">{value}</p>
    </div>
  )
}

interface SummaryGridProps {
  entitlement: Entitlement
  t: TFunction
}

export function SummaryGrid({ entitlement, t }: SummaryGridProps): ReactElement {
  const validUntil = entitlement.validUntil
    ? new Date(entitlement.validUntil).toLocaleDateString()
    : t('dualGate.perpetual')
  return (
    <div className="mt-6 rounded-lg bg-stone-50 p-4 dark:bg-stone-900">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryField label={t('dualGate.plan')} value={entitlement.planTierGranted} />
        <SummaryField
          label={t('dualGate.seats')}
          value={`${String(entitlement.seatsUsed)} / ${String(entitlement.seatsGranted)}`}
        />
        <SummaryField
          label={t('dualGate.validFrom')}
          value={new Date(entitlement.validFrom).toLocaleDateString()}
        />
        <SummaryField label={t('dualGate.validUntil')} value={validUntil} />
      </div>
    </div>
  )
}

interface ActionButtonProps {
  onClick: () => void
  isLoading: boolean
  icon: ReactNode
  label: string
}

function ActionButton({ onClick, isLoading, icon, label }: ActionButtonProps): ReactElement {
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {label}
    </button>
  )
}

interface ActionButtonsProps {
  entitlement: Entitlement
  isLoading: boolean
  onPassPaymentGate?: (() => void) | undefined
  onPassLicenseGate?: (() => void) | undefined
  onActivate?: (() => void) | undefined
  t: TFunction
}

export function ActionButtons({
  entitlement,
  isLoading,
  onPassPaymentGate,
  onPassLicenseGate,
  onActivate,
  t,
}: ActionButtonsProps): ReactElement {
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      {!entitlement.paymentGatePassed && onPassPaymentGate && (
        <ActionButton
          onClick={onPassPaymentGate}
          isLoading={isLoading}
          icon={<CreditCard className="h-4 w-4" />}
          label={t('dualGate.passPaymentGate')}
        />
      )}
      {entitlement.paymentGatePassed && !entitlement.licenseGatePassed && onPassLicenseGate && (
        <ActionButton
          onClick={onPassLicenseGate}
          isLoading={isLoading}
          icon={<Key className="h-4 w-4" />}
          label={t('dualGate.passLicenseGate')}
        />
      )}
      {entitlement.gatesPassed && entitlement.status === 'pending' && onActivate && (
        <ActionButton
          onClick={onActivate}
          isLoading={isLoading}
          icon={<Shield className="h-4 w-4" />}
          label={t('dualGate.activateEntitlement')}
        />
      )}
      {entitlement.status === 'active' && (
        <div className="flex items-center gap-2 rounded-lg bg-[#4e5e82]/10 px-4 py-2 text-sm font-medium text-[#5c6e94] dark:bg-[#8a9bba]/15 dark:text-[#8a9bba]">
          <CheckCircle className="h-4 w-4" />
          {t('dualGate.fullyActivated')}
        </div>
      )}
    </div>
  )
}
